/**
 * Friendly mapping for generate-avatar failures.
 *
 * `supabase.functions.invoke` collapses EVERY non-2xx into the opaque
 * "Edge Function returned a non-2xx status code". The real status + typed error
 * code live on `FunctionsHttpError.context` (a Response), so we read the body
 * and map the code to consumer-facing copy.
 *
 * Server remains the authority: this only changes what the user READS.
 */
import { FunctionsHttpError } from "@supabase/supabase-js";

export type GenerationErrorCode =
  | "GENERATION_LIMIT_REACHED"
  | "INSUFFICIENT_CREDITS"
  | "AUTH_REQUIRED"
  | "CONSENT_REQUIRED"
  | "BIOMETRIC_CONSENT_REQUIRED"
  | "TERMS_CONSENT_REQUIRED"
  | "AGE_AFFIRMATION_REQUIRED"
  | "SESSION_INVALID"
  | "SESSION_CONTEXT_REQUIRED"
  | "FORBIDDEN"
  | "VALIDATION_ERROR"
  | "CONFLICT"
  | "RATE_LIMITED"
  | "UNKNOWN";

export interface GenerationErrorInfo {
  code: GenerationErrorCode;
  status: number | null;
  /** Consumer-facing copy — safe to render directly. */
  message: string;
  /** True when the user cannot retry without more allowance/credits. */
  isLimit: boolean;
  /** Server-declared retryability, with a conservative code-based fallback. */
  retryable: boolean;
  /** The funnel route that can resolve a non-retryable gate, when one exists. */
  recoveryRoute: "verify" | "selfie" | "style" | "home" | null;
}

function friendly(code: GenerationErrorCode, freeAttempts: number, serverMessage?: string): string {
  switch (code) {
    case "GENERATION_LIMIT_REACHED":
      return `You've used all ${freeAttempts} of your free generations.`;
    case "INSUFFICIENT_CREDITS":
      return "You're out of generation credits.";
    case "AUTH_REQUIRED":
      return "Please sign in again to keep going.";
    case "CONSENT_REQUIRED":
    case "BIOMETRIC_CONSENT_REQUIRED":
      return "We need your consent for this photo before we can create your face. Tick it on the consent step and we'll carry on.";
    case "TERMS_CONSENT_REQUIRED":
      return "We need you to accept the terms before we can create your face.";
    case "AGE_AFFIRMATION_REQUIRED":
      return "We need your age affirmation before we can create your face — it's one tick on the consent step.";
    case "SESSION_INVALID":
    case "SESSION_CONTEXT_REQUIRED":
      return "That session has expired — start again from the consent step.";
    case "FORBIDDEN":
      return "You don't have access to this action.";
    case "RATE_LIMITED":
      return "Too many requests just now — give it a moment and try again.";
    case "CONFLICT":
      return "That request is already running — hold on a moment.";
    case "VALIDATION_ERROR":
      return serverMessage || "Something in that request wasn't quite right.";
    default:
      return "We couldn't create your face just now. Please try again.";
  }
}

/**
 * Resolve any thrown/returned invoke error into typed, consumer-safe info.
 * Reads the response body off FunctionsHttpError.context (the ONLY place the
 * real status and error code survive).
 */
export async function describeGenerationError(
  err: unknown,
  freeAttempts: number,
): Promise<GenerationErrorInfo> {
  let status: number | null = null;
  let code: GenerationErrorCode = "UNKNOWN";
  let serverMessage: string | undefined;
  let serverRetryable: boolean | undefined;

  if (err instanceof FunctionsHttpError) {
    status = err.context?.status ?? null;
    try {
      const body = await err.context.clone().json();
      const raw = (body?.error_code ?? body?.code) as string | undefined;
      if (typeof raw === "string") code = raw as GenerationErrorCode;
      if (typeof body?.error === "string") serverMessage = body.error;
      else if (typeof body?.message === "string") serverMessage = body.message;
      if (typeof body?.retryable === "boolean") serverRetryable = body.retryable;
    } catch {
      /* non-JSON body — fall through to status-based mapping below */
    }
  } else if (typeof err === "object" && err !== null) {
    const anyErr = err as { status?: number; context?: { status?: number }; message?: string };
    status = anyErr.status ?? anyErr.context?.status ?? null;
    serverMessage = anyErr.message;
  }

  if (code === "UNKNOWN" && status === 402) code = "GENERATION_LIMIT_REACHED";
  if (code === "UNKNOWN" && status === 401) code = "AUTH_REQUIRED";
  if (code === "UNKNOWN" && status === 429) code = "RATE_LIMITED";

  const isLimit = code === "GENERATION_LIMIT_REACHED" || code === "INSUFFICIENT_CREDITS";
  const retryableByCode = code === "UNKNOWN" || code === "RATE_LIMITED" || code === "CONFLICT";
  return {
    code,
    status,
    message: friendly(code, freeAttempts, serverMessage),
    isLimit,
    retryable: serverRetryable ?? retryableByCode,
    /**
     * A gate that a person CAN clear gets the route that clears it — naming the
     * gap without offering the remedy is a dead end with better copy. Only
     * genuinely unactionable failures (limits, authorisation) fall back to home.
     */
    recoveryRoute:
      code === "CONSENT_REQUIRED" ||
      code === "BIOMETRIC_CONSENT_REQUIRED" ||
      code === "TERMS_CONSENT_REQUIRED" ||
      code === "AGE_AFFIRMATION_REQUIRED" ||
      code === "SESSION_INVALID" ||
      code === "SESSION_CONTEXT_REQUIRED"
        ? "verify"
        : code === "AUTH_REQUIRED" || code === "FORBIDDEN" || isLimit
          ? "home"
          : null,
  };
}
